import { Injectable, inject, signal, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { firstValueFrom } from 'rxjs';

export interface UserProfile {
    name: string;
    role: string;
    email: string;
    phone: string;
    address: string;
    avatar: string;
    companyId?: string;
    username?: string;
}

/** Backend now returns PostgreSQL format (not Cognito Attributes) */
interface BackendUser {
    username: string;
    email: string;
    firstName?: string;
    lastName?: string;
    companyId: number;
    enabled: boolean;
    roles: string[];
    avatarUrl?: string;
    phoneNumber?: string;
    address?: string;
    contacts?: any[];
    created?: string;
    updated?: string;
}

const PROFILE_CACHE_KEY = 'pos_user_profile';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private http = inject(HttpClient);
    private authService = inject(AuthService);
    private platformId = inject(PLATFORM_ID);

    readonly profile = signal<UserProfile>({
        name: '',
        role: '',
        email: '',
        phone: '',
        address: '',
        avatar: ''
    });

    readonly isLoading = signal(false);
    readonly error = signal<string | null>(null);

    private get isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    /** Load user profile from backend or cache */
    async loadProfile(): Promise<void> {
        // Try cache first for instant display
        if (this.isBrowser) {
            const cached = localStorage.getItem(PROFILE_CACHE_KEY);
            if (cached) {
                try {
                    this.profile.set(JSON.parse(cached));
                } catch { /* ignore */ }
            }
        }

        this.isLoading.set(true);
        this.error.set(null);

        try {
            const currentUser = this.authService.currentUser();
            if (!currentUser?.email) {
                await this.buildProfileFromAuth();
                return;
            }

            // Fetch from backend (PostgreSQL format)
            const username = currentUser.email;
            const backendUser = await firstValueFrom(
                this.http.get<BackendUser>(`${environment.apiUrls.users}/users/${encodeURIComponent(username)}`)
            );

            const profile = this.mapBackendUser(backendUser, currentUser);
            this.profile.set(profile);
            this.cacheProfile(profile);
        } catch (err: any) {
            console.warn('[UserService] Failed to load from backend, using auth data:', err?.message);
            await this.buildProfileFromAuth();
        } finally {
            this.isLoading.set(false);
        }
    }

    /** Update profile locally (and optionally sync to backend) */
    updateProfile(newProfile: UserProfile) {
        this.profile.set(newProfile);
        this.cacheProfile(newProfile);
    }

    private async buildProfileFromAuth(): Promise<void> {
        const currentUser = this.authService.currentUser();
        const userName = await this.authService.getUserName();

        const profile: UserProfile = {
            name: userName || currentUser?.name || '',
            role: '',
            email: currentUser?.email || '',
            phone: '',
            address: '',
            avatar: currentUser?.avatar || '',
            companyId: currentUser?.companyId || '',
            username: currentUser?.email || ''
        };

        this.profile.set(profile);
        this.cacheProfile(profile);
    }

    /** Map PostgreSQL backend response to UserProfile */
    private mapBackendUser(user: BackendUser, authAccount: { email: string; name: string; avatar: string; companyId: string }): UserProfile {
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
            || authAccount.name
            || user.username;
        const role = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : '';

        return {
            name,
            role,
            email: user.email || authAccount.email || '',
            phone: user.phoneNumber || '',
            address: user.address || '',
            avatar: user.avatarUrl || authAccount.avatar || '',
            companyId: String(user.companyId || '') || authAccount.companyId || '',
            username: user.username
        };
    }

    private cacheProfile(profile: UserProfile): void {
        if (this.isBrowser) {
            localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
        }
    }

    /** Clear cache on logout */
    clearCache(): void {
        if (this.isBrowser) {
            localStorage.removeItem(PROFILE_CACHE_KEY);
        }
    }
}
